import React, { useMemo, useState } from "react";
import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaYoutube
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { trackCtaClick } from "../../utils/analytics";
import Icon from "../icons/Icon";
import styles from "./FloatingSocialBar.module.css";

/*
 * FloatingSocialBar — SOCIAL PROFILES ONLY.
 *
 * Direct-contact links (WhatsApp, Call, Email) used to live here too. They now
 * belong to ContactDock, which is the single place a visitor starts a
 * conversation. Nothing in this rail may duplicate them: two bottom-right
 * stacks both offering WhatsApp is exactly the "bolted-on widget" clutter this
 * corner was cleaned up to avoid.
 *
 * The rail is kept separate from ContactDock so each concern stays legible:
 * the dock converts, the rail is where you go to look us up. When this was
 * written the Footer carried no social links, which made this rail the site's
 * ONLY route to Appinfigo's profiles — check that before ever removing it.
 *
 * Every entry is the global 44px box (.btn .btn--box) — paper-filled under an
 * ink outline so it reads over paper, olive and void sections alike — and the
 * marks are MONOCHROME: ink on paper, inverting to paper on ink on hover. No
 * platform colour appears anywhere in this rail.
 *
 * The marks are filled glyphs the platforms own, so they come from react-icons
 * rather than the site's 1.5-stroke Icon set (imported components, so no SVG
 * is hand-written here); they sit a touch smaller to match it optically. The
 * toggle uses the site icon.
 */

// TODO(content): real Appinfigo profiles — keep these hrefs in sync.
const SOCIAL_LINKS = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/appinfigo/",
    glyph: FaLinkedinIn
  },
  {
    label: "X",
    href: "https://x.com/appinfigo",
    glyph: FaXTwitter
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/appinfigo",
    glyph: FaInstagram
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/appinfigo",
    glyph: FaFacebookF
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/@appinfigo-yt",
    glyph: FaYoutube
  }
];

// TODO(content): UI labels. WCAG 2.5.3: linkName() must keep containing the
// platform label verbatim, because that label is what the hover panel shows.
const LABELS = {
  region: "Appinfigo on social media",
  linkName: (platform) => `Appinfigo on ${platform}`,
  showMore: "Show more social links",
  showFewer: "Show fewer social links"
};

const DEFAULT_VISIBLE_COUNT = 3;
const MAX_STAGGER_STEPS = 4;
/* 17px, not the 18px of the toggle's stroke icon: a filled mark reads heavier. */
const BRAND_GLYPH_SIZE = 17;

function FloatingSocialBar() {
  const [isExpanded, setIsExpanded] = useState(false);

  const visibleLinks = useMemo(() => {
    if (isExpanded) {
      return SOCIAL_LINKS;
    }

    return SOCIAL_LINKS.slice(0, DEFAULT_VISIBLE_COUNT);
  }, [isExpanded]);

  const hasHiddenLinks = SOCIAL_LINKS.length > DEFAULT_VISIBLE_COUNT;

  const handleSocialClick = (item) => {
    trackCtaClick(item.label, "floating_social", item.href);
  };

  return (
    /* data-surface, never data-tone: data-tone is the Header's signal and
       belongs to Sections only. */
    <aside
      className={styles.floatingSocialBar}
      aria-label={LABELS.region}
      data-analytics-source="social_rail"
      data-surface="paper"
    >
      <div className={styles.floatingSocialShell}>
        {visibleLinks.map((item, index) => {
          const BrandGlyph = item.glyph;
          const stagger = Math.min(index, MAX_STAGGER_STEPS - 1);

          return (
            <a
              key={item.label}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className={`btn btn--box ${styles.floatingSocialLink}`}
              aria-label={LABELS.linkName(item.label)}
              style={{ animationDelay: `${stagger * 0.06}s` }}
              onClick={() => handleSocialClick(item)}
            >
              <BrandGlyph
                size={BRAND_GLYPH_SIZE}
                className={styles.brandGlyph}
                aria-hidden="true"
                focusable="false"
              />
              <span className={`micro ${styles.floatingSocialLabel}`}>{item.label}</span>
            </a>
          );
        })}

        {hasHiddenLinks ? (
          <button
            type="button"
            className={`btn btn--box ${styles.floatingSocialToggle} ${
              isExpanded ? styles.expanded : ""
            }`}
            aria-label={isExpanded ? LABELS.showFewer : LABELS.showMore}
            aria-expanded={isExpanded}
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            <Icon name="chevron-down" size={18} className={styles.toggleGlyph} />
          </button>
        ) : null}
      </div>
    </aside>
  );
}

export default FloatingSocialBar;
